import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import mapValues from 'lodash.mapvalues';
import merge from 'lodash.merge';
import omit from 'lodash.omit';
import Select from '../../../components/Select/Select';
import {
  ASYNC_FIELDS,
  initialValues as defaultInitialValues,
  inventoryForm
} from '../../../schemas/forms/inventory.schema';
import { CONTENT_TYPE } from '../../../enums';
import LoadableSelect from '../../../components/Select/LoadableSelect';
import {
  inventoryFilter,
  inventoryMakes,
  inventoryModels,
  inventoryYears
} from '../../../service/Inventory.service';
import { handleError } from '../../../service/base.service';
import GroupRadio from '../../../components/GroupRadio/GroupRadio';
import Button from '../../../components/Button/Button';
import InputText from '../../../components/Input/InputText';
import TextArea from '../../../components/TextArea/TextArea';
import Checkbox from '../../../components/Checkbox/Checkbox';
import {
  digitsOnly,
  filterIsUpdated,
  getValueOrDefault,
  groupModelsByMakeOrder,
  makesListIsUpdated,
  yearIsUpdated
} from './helper';
import Input from '../../../components/Input/Input';
import DealershipAccountField from '../../../components/DealershipAccountField/DealershipAccountField';
import { ReactComponent as IconAutoFixHigh } from '../../../public/images/svg/icon_auto_fix_high.svg';
import { getCurrentDate } from '../../../utils/date';

const {
  REACT_APP_INVENTORY_DEFAULT_DATE,
  REACT_APP_INVENTORY_DEFAULT_DATE_NEW_OFFSET,
  REACT_APP_INVENTORY_DEFAULT_DATE_USED_OFFSET
} = process.env;

const setDefaultYear = (inventoryType) => {
  const defaultYear = getValueOrDefault(
    REACT_APP_INVENTORY_DEFAULT_DATE,
    getCurrentDate().getFullYear()
  );
  const newYearOffset = getValueOrDefault(REACT_APP_INVENTORY_DEFAULT_DATE_NEW_OFFSET, '0');
  const usedYearOffset = getValueOrDefault(REACT_APP_INVENTORY_DEFAULT_DATE_USED_OFFSET, '0');

  const newYear = parseInt(defaultYear, 10) + parseInt(newYearOffset, 10);
  const usedYear = newYear + parseInt(usedYearOffset, 10);

  return inventoryType === 'new'
    ? new Date(newYear.toString()).getFullYear()
    : new Date(usedYear.toString()).getFullYear();
};

const parseInventoryFormData = (form, modelsList) => {
  return {
    ...form,
    vehicles: form.vehicles.reduce((vehicles, vehicle, index) => {
      const { makeId, makeName, models } = vehicle;

      if (makeId && makeName) {
        vehicles.push({
          makeId,
          makeName,
          models: models.length ? models : modelsList[index]
        });
      }

      return vehicles;
    }, [])
  };
};

const InventoryForm = ({ contentType, handleContentType, onSubmit, initialValues }) => {
  const navigate = useNavigate();
  const [yearsList, setYearsList] = useState([]);
  const [filtersList, setFiltersList] = useState({});
  const [makesList, setMakesList] = useState([]);
  const [modelsList, setModelList] = useState([]);
  const [isFetchingMakes, setFetchingMakes] = useState(false);
  const [isFetchingModels, setFetchingModels] = useState(false);
  const [isFetchingFilters, setFetchingFilters] = useState(false);

  const previousSelectedYear = useRef();
  const previousSelectedMakesList = useRef();
  const previousSelectedFilter = useRef();

  const formik = useFormik({
    initialValues: {
      ...defaultInitialValues,
      contentType,
      year: setDefaultYear(defaultInitialValues.inventoryType)
    },
    validationSchema: inventoryForm,
    validateOnChange: false,
    validateOnBlur: true,
    validateOnMount: false,
    onSubmit: (form) => {
      onSubmit(
        inventoryForm.cast(parseInventoryFormData(form, modelsList), { stripUnknown: true })
      );
    },
    onReset: () => {
      setFiltersList({});
      setModelList([]);
      previousSelectedMakesList.current = undefined;
      previousSelectedFilter.current = undefined;
    }
  });

  const { setValues, setTouched, validateForm } = formik;

  useEffect(() => {
    const exec = async () => {
      try {
        const res = await inventoryYears();
        setYearsList(res?.data);
      } catch (e) {
        handleError(e, {
          UnauthorizedError: navigate,
          default: () => console.error(e)
        });
      }
    };

    if (formik.values.contentType === CONTENT_TYPE.INVENTORY.value) {
      exec();
    }
  }, [formik.values?.contentType]);

  useEffect(() => {
    if (yearIsUpdated([formik.values?.year], [previousSelectedYear?.current])) {
      const exec = async () => {
        setFetchingMakes(true);
        try {
          const res = await inventoryMakes({ yearId: formik.values?.year });

          setMakesList(res?.data ?? []);
          previousSelectedYear.current = formik.values?.year;
        } catch (e) {
          handleError(e, {
            UnauthorizedError: navigate,
            default: () => console.error(e)
          });
        } finally {
          setFetchingMakes(false);
        }
      };
      exec();
    }
  }, [formik.values]);

  useEffect(() => {
    if (makesListIsUpdated(formik.values?.vehicles, previousSelectedMakesList?.current)) {
      setFetchingFilters(true);
      const makes = formik.values?.vehicles?.filter((make) => !!make).map((make) => make?.makeId);

      if (makes.length) {
        const exec = async () => {
          try {
            const res = await inventoryFilter({
              yearId: formik.values?.year,
              makes
            });
            setFiltersList(res?.data ?? {});
            const selectedCarsList = formik.values?.vehicles;

            previousSelectedMakesList.current = [...selectedCarsList];
          } catch (e) {
            handleError(e, {
              UnauthorizedError: navigate,
              default: () => console.error(e)
            });
          } finally {
            setFetchingFilters(false);
          }
        };
        exec();
      } else {
        setFiltersList({});
      }
    }
  }, [formik.values]);

  useEffect(() => {
    if (
      makesListIsUpdated(formik.values?.vehicles, previousSelectedMakesList?.current) ||
      filterIsUpdated(formik.values?.filter, previousSelectedFilter?.current)
    ) {
      setFetchingModels(true);
      const makes = formik.values?.vehicles?.filter((make) => !!make).map((make) => make?.makeId);

      if (makes.length) {
        const exec = async () => {
          try {
            const res = await inventoryModels({
              yearId: formik.values?.year,
              makes,
              filters: formik.values?.filter
            });

            previousSelectedMakesList.current = [...formik.values?.vehicles];
            previousSelectedFilter.current = formik.values?.filter;

            const groupedModelsList = groupModelsByMakeOrder(formik.values?.vehicles, res.data);
            setModelList(groupedModelsList);
          } catch (e) {
            handleError(e, {
              UnauthorizedError: navigate,
              default: () => console.error(e)
            });
          } finally {
            setFetchingModels(false);
          }
        };
        exec();
      } else {
        setModelList([]);
      }
    }
  }, [formik.values]);

  const getConfigurableYearList = useCallback(
    () => yearsList?.map(({ yearId: year }) => ({ label: year, value: year })),
    [yearsList]
  );

  const getMakesList = useCallback(() => {
    return (makesList ?? []).reduce((newList, { makeId, makeName }) => {
      const item = { label: makeName, value: { makeId, makeName } };
      const hasBeenSelected = formik.values?.vehicles?.some((selectedMake) => {
        return Number(selectedMake?.makeId) === Number(makeId);
      });

      if (!hasBeenSelected) {
        newList.push(item);
      }
      return newList;
    }, []);
  }, [formik.values?.vehicles, makesList]);

  const getBodyStyleFilterList = useCallback(() => {
    return filtersList?.bodyStyles?.map((bodyStyle) => ({ label: bodyStyle, value: bodyStyle }));
  }, [filtersList]);

  const getSizeFilterList = useCallback(() => {
    return filtersList?.sizes?.map((size) => ({ label: size, value: size }));
  }, [filtersList]);

  const getFuelTypeFilterList = useCallback(() => {
    return filtersList?.fuelTypes?.map((fuelType) => ({ label: fuelType, value: fuelType }));
  }, [filtersList]);

  const getModelsList = useCallback(
    (index) => {
      return (modelsList?.[index] ?? [])
        .filter(({ modelId }) => {
          return !formik.values?.vehicles?.[index]?.models.find(
            ({ modelId: selectedModelId }) => Number(selectedModelId) === Number(modelId)
          );
        })
        .map(({ modelId, modelName }) => ({
          label: modelName,
          value: { modelId, modelName }
        }));
    },
    [formik.values?.vehicles, modelsList]
  );

  const setFormikVehicleValues = (formik) => {
    formik.setFieldValue(
      'vehicles',
      formik.values.vehicles.map((vehicle) => ({ ...vehicle, models: [] }))
    );
  };

  useEffect(() => {
    if (!initialValues) {
      return;
    }

    // make sure that inline object goes first, not to merge into defaultInitialValues directly as it will mutate it
    const formValues = merge(
      { contentType },
      defaultInitialValues,
      // we will not set the new nested fields for now as they need special treatment (to be addressed in US1512082)
      // omit(initialValues, ['filter', 'vehicles'])
      initialValues
    );
    const touchFields = (v) => (typeof v === 'object' ? mapValues(v, touchFields) : true);
    const touchedFields = touchFields(formValues);

    // don't touch async fields to avoid showing errors before they reach a consistent state
    setValues(formValues)
      .then(() => setTouched(omit(touchedFields, ASYNC_FIELDS)))
      .then(() => validateForm(omit(formValues, ASYNC_FIELDS)));
  }, [initialValues, contentType, setValues, setTouched, validateForm]);

  const handleOptionsRejectedCallback = (e) => {
    handleError(e, {
      UnauthorizedError: navigate,
      default: () => console.error(e)
    });
  };

  return (
    <div
      className="flex flex-wrap flex-auto justify-between align-center mt-4 gap-8"
      data-testid="inventory-form"
    >
      <div className="w-screen max-h-max min-w-[347px] grow bg-white">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-400 to-gradient-blue-purple-100" />
        <div className="max-w-md p-6 -mb-8">
          <Select
            id="content-type"
            label="Content Type"
            value={formik.values.contentType}
            onChange={(value) => {
              formik.setFieldValue('contentType', value);
              handleContentType(value);
            }}
            onBlur={formik.handleBlur}
            options={CONTENT_TYPE.toArray()}
            required
            dataTest="inventory-form-content-type"
            disabled={!!initialValues}
          />
        </div>
        <div className="px-6 flex grow">
          <div className="w-[25rem] mr-8">
            <Select
              label="Year"
              id="year"
              required
              value={formik.values.year}
              onChange={(value) => {
                formik.setFieldValue('year', value);
                formik.setFieldValue('vehicles', []);
                formik.setFieldValue('filter', { bodyStyles: [], sizes: [], fuelTypes: [] });
                setFiltersList({});
                setModelList([]);
                previousSelectedMakesList.current = undefined;
                previousSelectedFilter.current = undefined;
              }}
              onBlur={formik.handleBlur}
              disabled={!formik.values.contentType}
              options={getConfigurableYearList()}
              error={formik.touched?.year && formik.errors?.year}
              searchable
              dataTest="inventory-form-year"
            />
          </div>
          <GroupRadio
            defaultValue={formik.values?.inventoryType}
            onChange={(value) => {
              formik.setFieldValue('inventoryType', value);
              formik.setFieldValue('year', setDefaultYear(value));
              formik.setFieldValue('vehicles', []);
              formik.setFieldValue('filter', {
                bodyStyles: [],
                sizes: [],
                fuelTypes: []
              });
              previousSelectedMakesList.current = undefined;
              previousSelectedFilter.current = undefined;
            }}
            error={formik.touched?.inventoryType && formik.errors?.inventoryType}
            dataTest="inventory-form-inventoryType"
            onBlur={formik.handleBlur}
            items={[
              {
                label: 'New',
                value: 'new',
                id: 'inventoryType'
              },
              {
                label: 'Used',
                value: 'used',
                id: 'inventoryType'
              }
            ]}
          />
        </div>
        <div className="px-6 flex flex-col w-full">
          <div className="flex flex-row justify-between">
            {Array(4)
              .fill(null)
              .map((_, index) => {
                const { makeId, makeName } = formik.values?.vehicles?.[index] ?? {};
                const value =
                  makeId && makeName ? { label: makeName, value: { makeId, makeName } } : null;
                let error = null;

                if (Array.isArray(formik.errors?.vehicles)) {
                  error =
                    typeof formik.errors?.vehicles?.[index] === 'string'
                      ? formik.errors?.vehicles?.[index]
                      : null;
                } else if (index === 0) {
                  error =
                    typeof formik.errors?.vehicles === 'string' ? formik.errors?.vehicles : null;
                }

                return (
                  <div key={`make-${index}`} className="w-full">
                    <LoadableSelect
                      label="Make"
                      required={index === 0}
                      id={`make.${index}`}
                      value={value}
                      disabled={!formik.values.year}
                      onChange={(value) => {
                        const newValue = [
                          ...formik.values?.vehicles.map((vehicle) => ({ ...vehicle, models: [] }))
                        ];
                        newValue[index] = value ? { ...value, models: [] } : null;
                        formik.setFieldValue('vehicles', newValue);
                        formik.setFieldValue('filter', {
                          bodyStyles: [],
                          sizes: [],
                          fuelTypes: []
                        });
                        setModelList([]);
                        setFiltersList({});
                      }}
                      onBlur={formik.handleBlur}
                      options={getMakesList}
                      optionsErrorCallback={handleOptionsRejectedCallback}
                      error={error}
                      searchable
                      dataTest={`inventory-form-make-${index}`}
                      isClearable
                      isLoading={isFetchingMakes}
                    />
                  </div>
                );
              })}
          </div>
        </div>
        <div className="px-6 pb-6 flex flex-col w-full">
          <div className="pb-4 w-full">Dynamic Filters</div>
          <div className="flex flex-row justify-between">
            <div className="w-1/3">
              <LoadableSelect
                label="Body Style"
                id="bodystyles"
                value={formik.values?.filter?.bodyStyles}
                onChange={(value) => {
                  formik.setFieldValue('filter.bodyStyles', value ?? []);
                  setFormikVehicleValues(formik);
                  setModelList([]);
                }}
                onBlur={formik.handleBlur}
                isLoading={isFetchingFilters}
                disabled={
                  !formik.values.vehicles?.filter((vehicle) => !!vehicle)?.length ||
                  !!formik.values?.filter?.fuelTypes?.length ||
                  !!formik.values?.filter?.sizes?.length
                }
                options={getBodyStyleFilterList}
                optionsErrorCallback={handleOptionsRejectedCallback}
                error={formik.touched?.filter?.bodystyle && formik.errors?.filter?.bodystyle}
                searchable
                dataTest="inventory-form-filter-bodystyle"
                isClearable
                isMulti
              />
            </div>
            <div className="w-1/3">
              <LoadableSelect
                label="Size"
                id="size"
                value={formik.values?.filter?.sizes}
                onChange={(value) => {
                  formik.setFieldValue('filter.sizes', value ?? []);
                  setFormikVehicleValues(formik);
                  setModelList([]);
                }}
                onBlur={formik.handleBlur}
                isLoading={isFetchingFilters}
                disabled={
                  !formik.values.vehicles?.filter((vehicle) => !!vehicle).length ||
                  !!formik.values?.filter?.bodyStyles?.length ||
                  !!formik.values?.filter?.fuelTypes?.length
                }
                options={getSizeFilterList}
                optionsErrorCallback={handleOptionsRejectedCallback}
                error={formik.touched?.filter?.size && formik.errors?.filter?.size}
                searchable
                dataTest="inventory-form-filter-size"
                isClearable
                isMulti
              />
            </div>
            <div className="w-1/3">
              <LoadableSelect
                label="Fuel Type"
                id="fueltype"
                value={formik.values?.filter?.fuelTypes}
                onChange={(value) => {
                  formik.setFieldValue('filter.fuelTypes', value ?? []);
                  setFormikVehicleValues(formik);
                  setModelList([]);
                }}
                onBlur={formik.handleBlur}
                isLoading={isFetchingFilters}
                disabled={
                  !formik.values.vehicles?.filter((vehicle) => !!vehicle).length ||
                  !!formik.values?.filter?.bodyStyles?.length ||
                  !!formik.values?.filter?.sizes?.length
                }
                options={getFuelTypeFilterList}
                optionsErrorCallback={handleOptionsRejectedCallback}
                error={formik.touched?.filter?.fueltype && formik.errors?.filter?.fueltype}
                searchable
                dataTest="inventory-form-filter-fueltype"
                isClearable
                isMulti
              />
            </div>
          </div>
        </div>
        <div className="px-6 flex flex-col w-full">
          <div className="flex flex-row justify-between">
            {Array(4)
              .fill(null)
              .map((_, index) => {
                const value = (formik.values?.vehicles?.[index]?.models ?? []).map(
                  ({ modelId, modelName }) => ({ label: modelName, value: { modelId, modelName } })
                );

                const error =
                  typeof formik.errors?.vehicles?.[index]?.models === 'string'
                    ? formik.errors?.vehicles?.[index]?.models
                    : null;
                return (
                  <div key={`model-${index}`} className="w-full">
                    <LoadableSelect
                      label="Model"
                      id={`model.${index}`}
                      value={value}
                      disabled={!formik.values.vehicles?.filter((vehicle) => !!vehicle).length}
                      onChange={(value) => {
                        const newValue = [...formik.values.vehicles];
                        newValue[index] = {
                          ...newValue[index],
                          models: value?.map((model) => model) ?? []
                        };
                        formik.setFieldValue('vehicles', newValue);
                      }}
                      isLoading={isFetchingModels}
                      onBlur={formik.handleBlur}
                      options={() => getModelsList(index)}
                      optionsErrorCallback={handleOptionsRejectedCallback}
                      error={error}
                      searchable
                      dataTest={`inventory-form-model-${index}`}
                      isClearable
                      isMulti
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
      <div className="w-3/12 max-h-max min-w-[347px] min-h-[690px] grow bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-400 to-gradient-blue-purple-300" />
        <div className="p-6">
          <DealershipAccountField
            label="Dealership Account Id"
            id="dealershipAccount"
            value={formik.values?.dealershipAccount}
            onChange={(dealershipAccount) => {
              formik
                .setFieldValue('dealershipAccount', dealershipAccount)
                .then(formik.validateField('dealershipAccount'))
                .then(() =>
                  // https://github.com/jaredpalmer/formik/issues/529#issuecomment-1462897295
                  setTimeout(
                    () => formik.setFieldTouched('dealershipAccount', !!dealershipAccount),
                    0
                  )
                );
            }}
            error={formik.touched?.dealershipAccount && formik.errors?.dealershipAccount?.id}
            maxLength={50}
            dataTest="inventory-form-dealership-account-id"
            bottomMarginSizing={1}
          />

          <TextArea
            label="Content Topic"
            id="contentTopic"
            value={formik.values?.contentTopic}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            required
            dataTest="inventory-form-content-topic"
            error={formik.touched?.contentTopic && formik.errors?.contentTopic}
          />
          <div className="grid grid-rows-1 grid-cols-2 gap-6 whitespace-nowrap">
            <div className="row-start-1 row-end-2 col-start-1 col-end-2">
              <Input
                label="Word Count"
                value={formik.values.wordCount}
                onChange={({ target: { value } }) => {
                  formik.setFieldValue('wordCount', value.replace(/[^\d]/, ''));
                }}
                onPaste={(evt) => {
                  formik.setFieldValue('wordCount', digitsOnly(evt));
                }}
                onBlur={formik.handleBlur}
                error={formik.touched?.wordCount && formik.errors?.wordCount}
                id="wordCount"
                maxLength={4}
                required
                dataTest="inventory-form-word-count"
                max="2500"
                min="100"
              />
            </div>
            <div className="row-start-1 row-end-2 col-start-2 col-end-3 flex justify-end">
              <Checkbox
                label="Check with Copyscape"
                value={formik.values?.executeCopyscapeSearch}
                onChange={({ target: { checked } }) => {
                  formik.setFieldValue('executeCopyscapeSearch', checked);
                }}
                onBlur={formik.handleBlur}
                type="checkbox"
                error={
                  formik.touched?.executeCopyscapeSearch && formik.errors?.executeCopyscapeSearch
                }
                id="copyscape"
                dataTest="inventory-form-copyscape"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-3/12 max-h-max min-w-[347px] grow bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-300 to-gradient-blue-purple-200" />
        <div className="p-6 -mb-8">
          <TextArea
            label="Target Audience or Market"
            id="targetAudience"
            value={formik.values?.targetAudience}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-target-audience"
            error={formik.touched?.targetAudience && formik.errors?.targetAudience}
          />
          <TextArea
            label="Geos"
            id="geos"
            value={formik.values?.geos}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-geos"
            error={formik.touched?.geos && formik.errors?.geos}
          />
          <TextArea
            label="Content Example 1"
            id="contentOne.content"
            value={formik.values?.contentOne?.content}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-content-one-content"
            error={formik.touched?.contentOne?.content && formik.errors?.contentOne?.content}
            placeholder="Place the copied content"
          />
          <InputText
            id="contentOne.writeStyle"
            value={formik.values?.contentOne?.writeStyle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-content-one-write-style"
            error={formik.touched?.contentOne?.writeStyle && formik.errors?.contentOne?.writeStyle}
            placeholder="What to take from example ex. tone, style, etc."
          />
          <TextArea
            label="Content Example 2"
            id="contentTwo.content"
            value={formik.values?.contentTwo?.content}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-content-two-content"
            error={formik.touched?.contentTwo?.content && formik.errors?.contentTwo?.content}
            placeholder="Place the copied content"
          />
          <InputText
            id="contentTwo.writeStyle"
            value={formik.values?.contentTwo?.writeStyle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-content-two-write-style"
            error={formik.touched?.contentTwo?.writeStyle && formik.errors?.contentTwo?.writeStyle}
            placeholder="What to take from example ex. tone, style, etc."
          />
        </div>
      </div>
      <div className="w-3/12 max-h-max min-w-[347px] min-h-[690px] grow bg-white overflow-hidden">
        <div className="bg-gradient-to-r block h-[2px] from-gradient-blue-purple-200 to-gradient-blue-purple-100" />
        <div className="p-6 -mb-8">
          <TextArea
            label="Additional Information"
            id="additionalInformation"
            value={formik.values?.additionalInformation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-additional-information"
            error={formik.touched?.additionalInformation && formik.errors?.additionalInformation}
          />
          <TextArea
            label="Negative Prompt"
            id="negativePrompt"
            value={formik.values?.negativePrompt}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-negative-prompt"
            error={formik.touched?.negativePrompt && formik.errors?.negativePrompt}
          />
          <InputText
            label="Link 1"
            id="linkOne.url"
            value={formik.values?.linkOne?.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-link-one-url"
            error={formik.touched?.linkOne?.url && formik.errors?.linkOne?.url}
            placeholder="ex. https://www.dealer.com/"
          />
          <InputText
            id="linkOne.description"
            value={formik.values?.linkOne?.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-link-one-description"
            error={formik.touched?.linkOne?.description && formik.errors?.linkOne?.description}
            placeholder="Add link description"
          />
          <InputText
            label="Link 2"
            id="linkTwo.url"
            value={formik.values?.linkTwo?.url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-link-two-url"
            error={formik.touched?.linkTwo?.url && formik.errors?.linkTwo?.url}
            placeholder="ex. https://www.dealer.com/"
          />
          <InputText
            id="linkTwo.description"
            value={formik.values?.linkTwo?.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            dataTest="inventory-form-link-two-description"
            error={formik.touched?.linkTwo?.description && formik.errors?.linkTwo?.description}
            placeholder="Add link description"
          />
        </div>
      </div>
      <div className="w-screen overflow-hidden p-4 flex justify-center gap-8">
        <Button
          type="submit"
          name="generate"
          dataTest="inventory-form-generate"
          className="flex justify-center items-center shadow-sm rounded-sm bg-buttonGradient py-4 px-16 text-white font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={formik.handleSubmit}
          icon={<IconAutoFixHigh className="mr-2" />}
          label="Generate"
          disabled={formik.isSubmitting}
        />
        <Button
          type="button"
          name="reset"
          dataTest="inventory-form-reset"
          className="flex justify-center bg-transparent py-4 text-purple-200 font-bold font-sans text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          onClick={formik.resetForm}
          label="Reset"
        />
      </div>
    </div>
  );
};

InventoryForm.propTypes = {
  contentType: PropTypes.string.isRequired,
  handleContentType: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default InventoryForm;
